import React from 'react'
import { reduxForm, Field } from 'redux-form'

import classNames from './common.css'
import Modal from './Modal'
import ConfirmButtons from '../ConfirmButtons'
import LoadingSpinner from '../LoadingSpinner'
import Checklist from '../Checklist'
import LocationPicker from '../search/LocationPicker'
import { guideSort } from '../../utils/array'

function EditUser ({ invalid, createError, handleSubmit, submitting, onCancel, title, showPasswordField, requirePassword, perms, libraries, hideAdminPerm, autoFocus }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <form onSubmit={handleSubmit} className={classNames.form} autoComplete='off'>
        <Field
          component={Input}
          props={{ label: 'Name' }}
          type='text'
          name='name'
          required
          autoFocus={autoFocus}
        />
        <Field
          component={Input}
          props={{ label: 'Email' }}
          type='text'
          name='email'
          required
        />
        {showPasswordField &&
          <Field
            component={Input}
            props={{
              label: 'Password',
              note: 'Passwords must be at least 8 characters long, and have at least one symbol, one uppercase letter and one lowercase letter.'
            }}
            type='text'
            name='password'
            required={requirePassword}
          />
        }
        <Field
          component={Input}
          props={{
            label: 'Default Location',
            component: LocationPicker,
            hideMap: true
          }}
          name='location'
          defaultValue='51.5,-0.13'
        />
        {perms &&
          <Field
            component={PermPicker}
            name='perms'
            defaultValue={[]}
            props={{ perms, hideAdminPerm }}
          />
        }
        {libraries &&
          <Field
            component={LibraryPicker}
            name='libraries'
            defaultValue={[]}
            props={{ libraries }}
          />
        }
        {invalid &&
          <p className={classNames.error} style={{ marginTop: '30px' }}>
            There is a problem with one or more fields, please fix the problem and try again
          </p>
        }
        <ConfirmButtons
          onCancel={onCancel}
          onConfirm={handleSubmit}
          confirmDisabled={submitting || invalid}
        />
        {createError &&
          <p className={classNames.centerError}>
            {getError(createError)}
          </p>
        }
        <LoadingSpinner show={submitting} />
      </form>
    </Modal>
  )
}

function Input (props) {
  const Component = props.component || 'input'
  return (
    <label>
      {props.label}
      {props.note &&
        <div className={classNames.note}>
          {props.note}
        </div>
      }
      <Component {...props} />
      {props.touched && props.error &&
        <div className={classNames.error}>
          {props.error}
        </div>
      }
    </label>
  )
}

const PERM_ORDER = [
  'view_collections',
  'search_and_save',
  'internal_share',
  'external_share',
  'extend_external_share',
  'flag_image',
  'download',
  'view_shared_channels',
  'create_channels',
  'dynamic_links',
  'image_config',
  // Admin perms
  'admin',
  'edit_collections',
  'internal_share_all',
  'no_download_restriction',
  'advanced_search',
  'advanced_info',
  'view_hierarchy'
]

// Map perms that should be selected when another perm is checked
const AUTO_SELECT_PERM = {
  'internal_share_all': 'internal_share',
  'search_and_save': 'view_collections'
}

function PermPicker ({ error, touched, value, onChange, perms, hideAdminPerm }) {
  const items = perms.filter(perm => {
    if (hideAdminPerm && perm.perm_token === 'admin') {
      return false
    }
    return true
  }).sort(guideSort(PERM_ORDER, 'perm_token')).map(perm => {
    return {
      value: perm.id,
      title: perm.perm_token,
      subtitle: perm.perm_desc,
      // Token here must be the token before admin section in PERM_ORDER
      append: perm.perm_token === 'image_config' ? <label>Admin permissions</label> : undefined
    }
  })
  return (
    <div>
      <label className={classNames.noBottomMargin}>
        Permissions
      </label>
      <Checklist
        items={items}
        value={value.map(perm => perm.perm.id)}
        onChange={(array, item) => {
          const newPerms = perms
            .filter(perm => array.indexOf(perm.id) !== -1)
            .map(perm => ({ perm }))
          for (let key of Object.keys(AUTO_SELECT_PERM)) {
            if (
              item.title === key &&
              newPerms.findIndex(p => p.perm.perm_token === AUTO_SELECT_PERM[key]) === -1
            ) {
              const sharePermIndex = perms.findIndex(p => p.perm_token === AUTO_SELECT_PERM[key])
              newPerms.push({ perm: perms[sharePermIndex] })
            }
          }
          onChange(newPerms)
        }}
      />
      {touched && error &&
        <div className={classNames.error}>
          {error}
        </div>
      }
    </div>
  )
}

function LibraryPicker ({ touched, error, value, onChange, libraries }) {
  const items = libraries.map(library => {
    const id = library.library.id
    const checked = value.findIndex(userLibrary => userLibrary.library.id === id) !== -1
    const onClick = () => {
      if (checked) {
        onChange(value.filter(userLibrary => userLibrary.library.id !== id))
      } else {
        onChange(value.concat(library).sort(sortByID))
      }
    }
    return (
      <button
        key={id}
        type='button'
        onClick={onClick}
        className={checked ? classNames.activeButton : classNames.button}>
        {library.library.library_token}
      </button>
    )
  })
  return (
    <div>
      <label className={classNames.noBottomMargin}>
        Libaries
      </label>
      {items}
      {touched && error &&
        <div className={classNames.error}>
          {error}
        </div>
      }
    </div>
  )
}

function getError (error) {
  if (!error) return null
  if (error.status === 409) {
    return 'A user with that email address already exists'
  }
  if (error.message) {
    return error.message
  }
  return `Error ${error.status}: ${error.statusText}`
}

function sortByID (a, b) {
  return a.id - b.id
}

function validate (values, props) {
  const errors = {}
  if (!values.name) {
    errors.name = 'A name is required'
  }
  if (!values.email || /.+@.+\..+/.test(values.email) === false) {
    errors.email = 'A valid email address is required'
  }
  if (!values.perms || !values.perms.length === 0) {
    errors.perms = 'At least one permission is required'
  }
  if (!values.libraries || !values.libraries.libraries === 0) {
    errors.libraries = 'At least one library is required'
  }
  if (props.requirePassword && !values.password) {
    errors.password = 'A password is required'
  }
  if (values.password) {
    if (/[^a-zA-Z0-9]/.test(values.password) === false) {
      errors.password = 'Password must have at least one symbol'
    }
    if (/[a-z]/.test(values.password) === false) {
      errors.password = 'Password must have at least one lowercase letter'
    }
    if (/[A-Z]/.test(values.password) === false) {
      errors.password = 'Password must have at least one uppercase letter'
    }
    if (values.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long'
    }
  }
  return errors
}

export default reduxForm({ form: 'user', validate })(EditUser)



// WEBPACK FOOTER //
// ./src/components/modals/EditUser.js